import moment from 'moment'

const ISO = 'YYYY-MM-DD'

/**
 * `new Date('2026-09-05')` parses as UTC midnight, which is the previous day
 * anywhere west of Greenwich -- and `ReportsPanel.handleDateChange` formats
 * whatever comes back through moment in local time. So a range that went
 * through the picker untouched would slide a day earlier. Parsing to local
 * midnight keeps the date string the same after the round trip.
 */
export const toLocalDate = (value) => moment(value, ISO).toDate()

export const formatDateLabel = (date) => moment(date).format('MMM D, YYYY')

/**
 * The reports date filter's presets. Every one includes both ends and ends
 * today, the same convention `defaultRange()` in `ReportsPanel.js` uses
 * ("14 days" means 14 calendar days shown, not 14 days back from yesterday).
 *
 * None goes past 90 days, deliberately: `RANGE_LIMIT_DAYS` in
 * `InvestigateTab.js` is 90 and `rangeTooLong` trips above it, so no preset
 * can hand the Investigate tab a range it then refuses to run.
 *
 * Each `range` takes "today" as an argument instead of reading the clock, so
 * callers can pin it and two calls in one render can't straddle midnight.
 */
const DEFINITIONS = [
    { id: 'last7', label: 'Last 7 days', range: (today) => [today.clone().subtract(6, 'days'), today.clone()] },
    { id: 'last14', label: 'Last 14 days', range: (today) => [today.clone().subtract(13, 'days'), today.clone()] },
    { id: 'last30', label: 'Last 30 days', range: (today) => [today.clone().subtract(29, 'days'), today.clone()] },
    { id: 'last90', label: 'Last 90 days', range: (today) => [today.clone().subtract(89, 'days'), today.clone()] },
    { id: 'thisMonth', label: 'This month', range: (today) => [today.clone().startOf('month'), today.clone()] },
    {
        id: 'lastMonth',
        label: 'Last month',
        range: (today) => [
            today.clone().subtract(1, 'month').startOf('month'),
            today.clone().subtract(1, 'month').endOf('month'),
        ],
    },
]

export const presetRanges = (today = moment()) =>
    DEFINITIONS.map((definition) => {
        const [start, end] = definition.range(today)
        return { id: definition.id, label: definition.label, startDate: start.format(ISO), endDate: end.format(ISO) }
    })

/**
 * The preset whose range is exactly `startDate`..`endDate`, or null. There
 * is no stored "selected preset" anywhere -- deriving it from the committed
 * range is what keeps the toolbar and Investigate instances in agreement.
 */
export const activePreset = (startDate, endDate, today = moment()) =>
    presetRanges(today).find((preset) => preset.startDate === startDate && preset.endDate === endDate) || null

/**
 * The trigger button's text. A matching preset gets its name plus the dates.
 * Anything else keeps the plain `<start> - <end>` label the control has
 * always shown.
 *
 * The year is dropped only in the preset case, and only when both ends fall
 * in the current year. Otherwise "Last 90 days" across New Year would read
 * "Oct 13 - Jan 10" with no way to tell which year is which.
 */
export const triggerLabel = (startDate, endDate, today = moment()) => {
    const preset = activePreset(startDate, endDate, today)
    if (!preset) {
        return `${formatDateLabel(startDate)} - ${formatDateLabel(endDate)}`
    }
    const start = moment(startDate, ISO)
    const end = moment(endDate, ISO)
    const format = start.year() === today.year() && end.year() === today.year() ? 'MMM D' : 'MMM D, YYYY'
    return `${preset.label} · ${start.format(format)} - ${end.format(format)}`
}
